import * as React from "react";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import { LoadingPage } from "../../../components/LoadingPage/LoadingPage";
import { SnackbarApiError } from "../../../components/SnackbarApiError/SnackbarApiError";
import { PowerBiEmbedConfig, PowerBiService } from "../../../gen/clients/llts";
import * as powerbi from "powerbi-client";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigation } from "../../../hooks/useNavigation";

interface PathParams {
  reportId: string;
}

const PowerBiReportEmbedPage: React.FC = () => {
  const { reportId } = useParams<PathParams>();
  const { t } = useTranslation();
  const { navigateTo } = useNavigation();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const serviceRef = React.useRef<powerbi.service.Service | null>(null);

  const { data, isLoading, error } = useQuery<PowerBiEmbedConfig>(["getPowerBiReportEmbedConfig", reportId], {
    queryFn: () => PowerBiService.getPowerBiReportEmbedConfig({ reportId })
  });

  const breadcrumbs = React.useMemo(
    () => [
      {
        label: t("crumbText.home"),
        path: "/",
        active: false
      },
      {
        label: t("pbiReports.title"),
        path: "/pbiReports",
        active: false
      },
      {
        label: data?.reportName ?? t("pbiReports.report")!,
        path: `/pbiReports/${reportId}`,
        active: true
      }
    ],
    [t, data?.reportName, reportId]
  );

  React.useEffect(() => {
    if (!data || !containerRef.current) {
      return;
    }

    if (!serviceRef.current) {
      serviceRef.current = new powerbi.service.Service(
        powerbi.factories.hpmFactory,
        powerbi.factories.wpmpFactory,
        powerbi.factories.routerFactory
      );
    }

    const powerbiService = serviceRef.current;
    const embedContainer = containerRef.current;
    powerbiService.reset(embedContainer);

    const embedConfig: powerbi.IEmbedConfiguration = {
      type: "report",
      tokenType: powerbi.models.TokenType.Embed,
      accessToken: data.accessToken,
      embedUrl: data.embedUrl,
      id: data.reportId,
      settings: {
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: true }
        }
      }
    };

    powerbiService.embed(embedContainer, embedConfig);

    return () => {
      powerbiService.reset(embedContainer);
    };
  }, [data]);

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <Container maxWidth="xl" sx={{ my: 4 }}>
      {error && <SnackbarApiError error={error} />}
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigateTo("/pbiReports")}
          sx={{ mb: 2 }}
        >
          {t("pbiReports.backToListing")}
        </Button>
        <Stack spacing={0.5} mb={2}>
          <Typography variant="h6">{data?.reportName}</Typography>
          <Typography variant="body2">{data?.reportDescription}</Typography>
        </Stack>
        <Box sx={{ height: "75vh", width: "100%", borderRadius: 1, overflow: "hidden", bgcolor: "background.paper" }}>
          <div ref={containerRef} style={{ height: "100%" }} />
        </Box>
      </Box>
    </Container>
  );
};

export { PowerBiReportEmbedPage };
